<template>
  <div class="vehicles-chart">
    <VueApexCharts
      ref="chartRef"
      type="bar"
      :height="chartHeight"
      :options="chartOptions"
      :series="series"
    ></VueApexCharts>
  </div>
</template>

<script setup lang="ts">
import VueApexCharts from 'vue3-apexcharts'
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import type { IVehicleAnalyticsAggregated } from '@/components/dashboard/types'

const props = defineProps<{
  data: IVehicleAnalyticsAggregated | null
  dataToCompare: IVehicleAnalyticsAggregated | null
}>()

// Responsive chart height
const chartHeight = ref(350)
const updateChartHeight = () => {
  if (window.innerWidth <= 599) {
    chartHeight.value = 250
  } else if (window.innerWidth <= 959) {
    chartHeight.value = 300
  } else {
    chartHeight.value = 350
  }
}

const updateChartOptions = () => {
  chartOptions.value.title.style.fontSize =
    window.innerWidth <= 599 ? '16px' : '18px'
  chartOptions.value.subtitle.style.fontSize =
    window.innerWidth <= 599 ? '12px' : '14px'
  chartOptions.value.stroke.width = window.innerWidth <= 599 ? 2 : 3
  chartOptions.value.markers.size = window.innerWidth <= 599 ? 4 : 5
  chartOptions.value.dataLabels.enabled = window.innerWidth > 599
  chartOptions.value.dataLabels.style.fontSize =
    window.innerWidth <= 599 ? '12px' : '14px'
  chartOptions.value.yaxis.title.style.fontSize =
    window.innerWidth <= 599 ? '12px' : '14px'
  chartOptions.value.yaxis.labels.style.fontSize =
    window.innerWidth <= 599 ? '10px' : '12px'
  chartOptions.value.xaxis.labels.style.fontSize =
    window.innerWidth <= 599 ? '10px' : '12px'
  chartOptions.value.noData.style.fontSize =
    window.innerWidth <= 599 ? '14px' : '16px'
}

onMounted(() => {
  updateChartHeight()
  updateChartOptions()
  window.addEventListener('resize', () => {
    updateChartHeight()
    updateChartOptions()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateChartHeight)
})

const series = ref<
  | {
      name: string
      data: number[]
    }[]
  | null
>(null)

const chartOptions = ref({
  chart: {
    type: 'line',
    height: 350,
    toolbar: {
      show: false, // Hide toolbar on mobile for cleaner look
    },
  },
  title: {
    text: 'Metrics Comparison',
    align: 'center',
    style: {
      fontSize: window.innerWidth <= 599 ? '16px' : '18px',
      fontWeight: 600,
    },
  },
  subtitle: {
    text: 'Last Month vs This Month',
    align: 'center',
    style: {
      fontSize: window.innerWidth <= 599 ? '12px' : '14px',
    },
  },
  stroke: {
    width: window.innerWidth <= 599 ? 2 : 3, // Thinner lines on mobile
    curve: 'smooth',
  },
  markers: {
    size: window.innerWidth <= 599 ? 4 : 5, // Smaller markers on mobile
  },
  dataLabels: {
    enabled: window.innerWidth > 599, // Disable data labels on very small screens
    formatter: function (val: number) {
      return val.toLocaleString()
    },
    style: {
      fontSize: window.innerWidth <= 599 ? '12px' : '14px',
      fontWeight: 'normal',
      colors: ['#000000'],
    },
  },
  yaxis: {
    title: {
      text: 'Count',
      style: {
        fontSize: window.innerWidth <= 599 ? '12px' : '14px',
      },
    },
    labels: {
      formatter: function (val: number) {
        return val.toLocaleString()
      },
      style: {
        fontSize: window.innerWidth <= 599 ? '10px' : '12px',
      },
    },
  },
  xaxis: {
    categories: ['Last Month', 'This Month'],
    labels: {
      style: {
        fontSize: window.innerWidth <= 599 ? '10px' : '12px',
      },
    },
  },
  noData: {
    text: 'Loading...',
    align: 'center',
    verticalAlign: 'middle',
    style: {
      fontSize: window.innerWidth <= 599 ? '14px' : '16px',
    },
  },
  colors: ['#3265ba', '#FFCC99', '#4CAF50'],
  responsive: [
    {
      breakpoint: 959,
      options: {
        chart: {
          height: 300,
        },
        title: {
          style: {
            fontSize: '16px',
          },
        },
        subtitle: {
          style: {
            fontSize: '12px',
          },
        },
      },
    },
    {
      breakpoint: 599,
      options: {
        chart: {
          height: 250,
        },
        title: {
          style: {
            fontSize: '14px',
          },
        },
        subtitle: {
          style: {
            fontSize: '11px',
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
    },
  ],
})

watch(
  () => [props.data, props.dataToCompare],
  ([newData, newDataToCompare]) => {
    const currentCount = newData?.count ?? 0
    const previousCount = newDataToCompare?.count ?? 0
    const currentImpressions = newData?.views ?? 0
    const previousImpressions = newDataToCompare?.views ?? 0
    const currentClicks = newData?.clicks ?? 0
    const previousClicks = newDataToCompare?.clicks ?? 0

    series.value = [
      {
        name: 'Vehicles',
        data: [previousCount, currentCount], // [Last Month, This Month]
      },
      {
        name: 'Impressions',
        data: [previousImpressions, currentImpressions],
      },
      {
        name: 'Clicks',
        data: [previousClicks, currentClicks],
      },
    ]
  },
  { immediate: true },
)
</script>
